import { BrowserAuthErrorCodes } from '@azure/msal-browser';

// localStorage, not sessionStorage: the login layout clears sessionStorage
// while logged out, which would lose this across the redirect.
export const SSO_REDIRECT_TO_KEY = 'tcap.sso.redirectTo';

export const POPUP_BLOCKED_MESSAGE =
    'Your browser blocked the Microsoft sign-in popup. Redirecting you to Microsoft to sign in instead...';

/** MSAL reports a blocked popup as one of these two error codes. */
export const isPopupBlockedError = (error) =>
    error?.errorCode === BrowserAuthErrorCodes.popupWindowError ||
    error?.errorCode === BrowserAuthErrorCodes.emptyWindowError;

/**
 * Signs in with a Microsoft popup, falling back to a full-page redirect with
 * the same request when the browser blocks the popup.
 *
 * @param {import('@azure/msal-browser').IPublicClientApplication} instance
 * @param {object} request  loginPopup request (scopes, state, redirectUri...).
 * @param {object} [options]
 * @param {function} [options.onBlocked] Called before redirecting.
 * @returns {Promise<object|null>} The popup result, or null when redirecting.
 */
export async function loginWithPopupFallback(
    instance,
    request,
    { onBlocked } = {},
) {
    try {
        return await instance.loginPopup(request);
    } catch (error) {
        if (!isPopupBlockedError(error)) throw error;

        onBlocked?.(error);
        const redirectTo = new URLSearchParams(window.location.search).get(
            'redirectTo',
        );
        if (redirectTo) {
            try {
                localStorage.setItem(SSO_REDIRECT_TO_KEY, redirectTo);
            } catch {
                // Storage unavailable: the redirect start page still carries it.
            }
        }
        await instance.loginRedirect({
            ...request,
            redirectStartPage: window.location.href,
        });
        return null;
    }
}

/** The redirectTo saved before a redirect sign-in, if any. */
export const getStoredRedirectTo = () => {
    try {
        return localStorage.getItem(SSO_REDIRECT_TO_KEY);
    } catch {
        return null;
    }
};

export const clearStoredRedirectTo = () => {
    try {
        localStorage.removeItem(SSO_REDIRECT_TO_KEY);
    } catch {
        // Nothing to clear.
    }
};
